import type { PropsWithChildren } from 'react';
import { Input } from '@ywwwtseng/react-kit';

export const FileTypes = {
  jpeg: 'image/jpeg',
  jpg: 'image/jpg',
  png: 'image/png',
  gif: 'image/gif',
};

const compressImage = async (
  file: File,
  options: { quality?: number; minSize?: number } = {
    quality: 1,
    minSize: undefined,
  }
): Promise<File> => {
  // 檢查文件格式
  if (![FileTypes.jpeg, FileTypes.jpg, FileTypes.png].includes(file.type)) {
    return file;
  }

  const quality = options.quality ?? 1;
  const minSize = options.minSize;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(img.src);

      let { width, height } = img;

      const canvas = document.createElement('canvas');

      // 計算縮放比例，確保最小邊不小於 30px
      if (minSize) {
        if (width < height) {
          height = (height * minSize) / width;
          width = minSize;
        } else {
          width = (width * minSize) / height;
          height = minSize;
        }
      }

      canvas.width = Math.round(width);
      canvas.height = Math.round(height);

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // 使用原始文件格式
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });

          resolve(compressedFile);
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
  });
};

interface FileInputProps extends PropsWithChildren {
  filetypes?: string[];
  className?: string;
  maxSize?: number;
  compress?: { minSize?: number; quality?: number } | boolean;
  onChange: (files: File[]) => void | Promise<void>;
  onError?: (error: Error) => void;
  multiple?: boolean;
}

function FileInput({
  children,
  filetypes = [FileTypes.jpeg, FileTypes.jpg, FileTypes.png],
  className,
  maxSize,
  compress = false,
  onChange,
  onError,
  multiple = false,
}: FileInputProps) {
  const handleChange = async (files: File[]) => {
    if (compress) {
      files = await Promise.all(
        files.map((file) =>
          compressImage(
            file,
            typeof compress === 'boolean' ? { quality: 1 } : compress
          )
        )
      );
    }

    if (
      filetypes.length > 0 &&
      !files.every((file) => filetypes.includes(file.type))
    ) {
      onError?.(new Error(`File type must be ${filetypes.join(', ')}!`));
      return;
    }

    if (maxSize && files.some((file) => file.size / 1024 / 1024 > maxSize)) {
      onError?.(new Error(`File size must be less than ${String(maxSize)}MB!`));
      return;
    }

    await onChange(files);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    await handleChange(Array.from(files));
    event.target.value = '';
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const files = event.dataTransfer.files;
    if (files.length === 0) {
      return;
    }

    await handleChange(Array.from(files));
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      className={className}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onDrop={(event) => {
        void handleDrop(event);
      }}
      onDragOver={handleDragOver}
    >
      <label>
        {children}
        <Input
          multiple={multiple}
          type="file"
          accept={filetypes.join(',')}
          className="hidden"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            void handleFileChange(event);
          }}
        />
      </label>
    </div>
  );
}

export { FileInput };
